import { useState, useEffect, useRef, useContext } from 'react'
import classnames from 'classnames/bind'
import { List, AutoSizer, CellMeasurerCache, CellMeasurer } from 'react-virtualized'
import { toast } from 'react-toastify'
import { useTranslation } from 'next-i18next'
import { isBoolean } from 'lodash-es'
import { useCookies } from 'react-cookie'

// Libs
import { handleMessageLimit } from '@/methods/message'

// Components
import Message from './components/Message'
import Popup from '@/components/common/Popup'

// Constants
import { COOKIE_DATA } from '@/constants/cookieKey'
import { STORAGE_DATA } from '@/constants/storageData'

// Contexts
import { LiveRoomContext } from '@/components/pages/LiveRoom/index'

// Styles
import styles from './style.module.scss'

const cx = classnames.bind(styles)

const cache: any = new CellMeasurerCache({
  defaultHeight: 22,
  minHeight: 22,
  fixedWidth: true,
})

export type MessageType = {
  name: string
  context: string
  type?: string
  isHost?: boolean
}

type MessageListProps = {
  className?: string
  socket: any
  messageList: MessageType[]
  roomPlacard?: string
  isBySelf: boolean
  setIsBySelf: any
  isLarge?: boolean
  isHost: boolean
  silentUserList: string[]
}

const MESSAGE_MARGIN = 2

const DEFAULT_LIMIT = { name: '', token: '', isLimited: false }

const MessageList = (props: MessageListProps) => {
  const { className, socket, messageList, isBySelf, setIsBySelf, isLarge, isHost, silentUserList } = props

  const { t } = useTranslation()
  const liveRoomContext: any = useContext(LiveRoomContext)

  const [isBottom, setIsBottom] = useState(false)
  const [currentLimit, setCurrentLimit] = useState(DEFAULT_LIMIT)

  const listRef: any = useRef(null)
  const limitMessagePopupRef: any = useRef(null)

  const canLimitMessage = isHost

  const [cookies] = useCookies([COOKIE_DATA.PCHOME_UUID, COOKIE_DATA.PCHOME_NAME_EXPIRED])
  const authToken = typeof window !== 'undefined' ? window.localStorage.getItem(STORAGE_DATA.TOKEN) : ''

  useEffect(() => {
    // 一進來滑到最新訊息
    setTimeout(() => {
      listRef.current?.scrollToRow(messageList.length - 1)
    }, 0)
  }, [])

  useEffect(() => {
    if (messageList.length > 0) {
      if (isBottom || isBySelf) {
        listRef.current.scrollToRow(messageList.length - 1)

        setIsBySelf(false)
      }
    }
  }, [messageList, isBottom, isBySelf])

  useEffect(() => {
    cache.clearAll()
    listRef.current.recomputeRowHeights()
  }, [isLarge])

  // PChome 合作案
  // local storage 的 token 更新，要重刷
  useEffect(() => {
    if (cookies?.[COOKIE_DATA.PCHOME_UUID] && !!authToken && isBoolean(liveRoomContext.isOpenSettingUserNamePopup)) {
      cache.clearAll()
      listRef.current.recomputeRowHeights()
    }
  }, [cookies?.[COOKIE_DATA.PCHOME_NAME_EXPIRED], cookies?.[COOKIE_DATA.PCHOME_UUID], authToken, liveRoomContext.isOpenSettingUserNamePopup])

  // PChome 合作案
  useEffect(() => {
    if (liveRoomContext.isRoomLivingDataSettled || liveRoomContext.isRoomInfoDataSettled) {
      cache.clearAll()
      listRef.current.recomputeRowHeights()
      liveRoomContext.setIsRoomLivingDataSettled(false)
      liveRoomContext.setIsRoomInfoDataSettled(false)
    }
  }, [liveRoomContext.isRoomLivingDataSettled, liveRoomContext.isRoomInfoDataSettled])

  const rowRenderer = ({ key, index, parent, style }: any) => {
    return (
      <CellMeasurer cache={cache} columnIndex={0} key={key} parent={parent} rowIndex={index}>
        <div style={style}>
          <Message
            className={cx('messageList-item')}
            {...messageList[index]}
            isLarge={isLarge}
            handleLimitClick={handleLimitClick}
            handleContextMenu={handleContextMenu}
            canLimitMessage={canLimitMessage}
            silentUserList={silentUserList}
          />
        </div>
      </CellMeasurer>
    )
  }

  const onRowsRendered = ({ stopIndex }: { stopIndex: number }) => {
    if (stopIndex === messageList.length - 1) {
      if (!isBottom) setIsBottom(true)
    } else {
      setIsBottom(false)
    }
  }

  const onResize = () => {
    cache.clearAll()
    listRef.current.recomputeRowHeights()
  }

  const handleContextMenu = (event: any) => {
    event.preventDefault()
  }

  const handleLimitClick = ({ name, token, isLimited }: { name: string; token: string; isLimited: boolean }) => {
    setCurrentLimit({ name, token, isLimited })

    limitMessagePopupRef.current.setIsPopupOpen(true)
  }

  return (
    <div className={cx('messageList', className)}>
      <AutoSizer className={cx('messageList-auto-sizer')} onResize={onResize}>
        {({ width, height }) => {
          return (
            <List
              ref={listRef}
              className={cx('messageList-list')}
              deferredMeasurementCache={cache}
              overscanRowCount={25}
              rowHeight={({ index }: any) => {
                // _rowHeightCache 是實際的高，目前只能這樣拿，不確定有沒有其他方法
                // 搭配 cache.clearAll() 跟 listRef.current.recomputeRowHeights() 在 resize 時都會重新計算高度
                // 算完高度後要再加上 margin
                return cache._rowHeightCache[`${index}-0`] > cache._minHeight
                  ? cache._rowHeightCache[`${index}-0`] + MESSAGE_MARGIN
                  : cache._rowHeightCache[`${index}-0`] || cache.rowHeight(index)
              }}
              width={width}
              height={height}
              rowCount={messageList.length}
              rowRenderer={rowRenderer}
              onRowsRendered={onRowsRendered}
            />
          )
        }}
      </AutoSizer>

      <Popup
        ref={limitMessagePopupRef}
        title={
          currentLimit.isLimited
            ? t('LiveRoom_Chat_Can_Unlimit', { name: currentLimit.name })
            : t('LiveRoom_Chat_Can_Limit', { name: currentLimit.name })
        }
        confirmLabel={currentLimit.isLimited ? t('LiveRoom_Chat_Can_Unlimit_Confirm') : t('LiveRoom_Chat_Can_Limit_Confirm')}
        onConfirm={() => {
          handleMessageLimit({ socket, userToken: currentLimit.token, canChat: currentLimit.isLimited ? 1 : -1 })

          const successMessage = currentLimit.isLimited ? t('LiveRoom_Chat_Can_Unlimit_Success') : t('LiveRoom_Chat_Can_Limit_Success')

          toast.success(successMessage)

          limitMessagePopupRef.current.setIsPopupOpen(false)
        }}
        onCancel={() => {
          setCurrentLimit(DEFAULT_LIMIT)
          limitMessagePopupRef.current.setIsPopupOpen(false)
        }}
      ></Popup>
    </div>
  )
}

export default MessageList
